import { ContentEditor } from "./ContentEditor";
import { MediaPicker } from "./MediaPicker";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
    title: z.string().min(2, {
        message: "Назва повинна містити щонайменше 2 символи",
    }),
    slug: z.string().min(2, {
        message: "URL повинен містити щонайменше 2 символи",
    }),
    content: z.string().min(10, {
        message: "Контент повинен містити щонайменше 10 символів",
    }),
    metaTitle: z.string(),
    metaDescription: z.string(),
    status: z.enum(["draft", "published"]),
    isIndexable: z.boolean(),
    template: z.string(),
    featuredImage: z.string().optional(),
});

interface PageEditorProps {
    isOpen: boolean;
    onClose: () => void;
    page?: any;
}

export const PageEditor = ({ isOpen, onClose, page }: PageEditorProps) => {
    const [activeTab, setActiveTab] = useState("content");

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: page?.title || "",
            slug: page?.slug || "",
            content: page?.content || "",
            metaTitle: page?.metaTitle || "",
            metaDescription: page?.metaDescription || "",
            status: page?.status || "draft",
            isIndexable: page?.isIndexable || true,
            template: page?.template || "default",
            featuredImage: page?.featuredImage || "",
        },
    });

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        console.log(values);
        // Тут буде логіка збереження
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl h-[80vh]">
                <DialogHeader>
                    <DialogTitle>
                        {page ? "Редагування сторінки" : "Створення нової сторінки"}
                    </DialogTitle>
                    <DialogDescription>
                        Заповніть необхідні поля для {page ? "редагування" : "створення"} сторінки
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="grid w-full grid-cols-4">
                                <TabsTrigger value="content">Контент</TabsTrigger>
                                <TabsTrigger value="seo">SEO</TabsTrigger>
                                <TabsTrigger value="media">Медіа</TabsTrigger>
                                <TabsTrigger value="settings">Налаштування</TabsTrigger>
                            </TabsList>

                            <ScrollArea className="h-[calc(80vh-15rem)] mt-4">
                                <TabsContent value="content" className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="title"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Назва сторінки</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Введіть назву сторінки" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="slug"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>URL сторінки</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="url-storіnky" {...field} />
                                                </FormControl>
                                                <FormDescription>
                                                    Використовуйте тільки латинські літери, цифри та дефіси
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="content"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Контент</FormLabel>
                                                <FormControl>
                                                    <ContentEditor
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </TabsContent>

                                <TabsContent value="seo" className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="metaTitle"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Meta Title</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="SEO заголовок" {...field} />
                                                </FormControl>
                                                <FormDescription>
                                                    Рекомендована довжина: 50-60 символів
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="metaDescription"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Meta Description</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="SEO опис"
                                                        className="min-h-[100px]"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormDescription>
                                                    Рекомендована довжина: 150-160 символів
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="isIndexable"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                <div className="space-y-0.5">
                                                    <FormLabel className="text-base">
                                                        Індексація пошуковими системами
                                                    </FormLabel>
                                                    <FormDescription>
                                                        Дозволити пошуковим системам індексувати цю сторінку
                                                    </FormDescription>
                                                </div>
                                                <FormControl>
                                                    <Switch
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </TabsContent>

                                <TabsContent value="media" className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="featuredImage"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Головне зображення</FormLabel>
                                                <FormControl>
                                                    <MediaPicker
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormDescription>
                                                    Рекомендований розмір: 1200x630px
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </TabsContent>

                                <TabsContent value="settings" className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="template"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Шаблон сторінки</FormLabel>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Оберіть шаблон" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="default">Стандартний</SelectItem>
                                                        <SelectItem value="landing">Лендінг</SelectItem>
                                                        <SelectItem value="blog">Блог</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription>
                                                    Оберіть шаблон для відображення контенту
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="status"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Статус</FormLabel>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Оберіть статус" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="draft">Чернетка</SelectItem>
                                                        <SelectItem value="published">Опубліковано</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription>
                                                    Визначте статус публікації сторінки
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </TabsContent>
                            </ScrollArea>
                        </Tabs>

                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={onClose}>
                                Скасувати
                            </Button>
                            <Button type="submit">Зберегти</Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}; 